(() => {
  if (window.ServiceifyAddToCartHandlerInitialized) return;

  const defaultConfig = {
    serviceTitle: "30-Minute Floral Expert Consultation",
    servicePrice: "$33.00",
  };

  const selectors = {
    servicifyModal: ".ReactModalPortal",
    buttonSelector: "[data-servicify=checkout]",
    overlaySelector: ".ReactModal__Overlay",
    consultationPackageButton: ".js-consultation-package-button",
    customButtonSelector:
      ".team-grid__button-wrapper a.ff-button, .real-weddings-gallery a.ff-button",
    scheduleButtonSelector: ".servicify-select-a-time-button",
    cartSelector: "[data-cart-sidebar]",
  };

  class ServiceifyAddToCartHandler {
    constructor() {
      const cartElement = document.querySelector(selectors.cartSelector);
      this.cart = cartElement ? cartElement.cloneNode(true) : null;
      this.maxAttempts = 3;
      this.config = { ...defaultConfig };
      this.variantData = null;
      this.packageButtonSelected = false;
      this.initialize();
    }

    initialize() {
      if (document.body) {
        this.loadVariantData();
        this.setupVariantListener();
        this.setupPackageButtonListeners();
        this.setupBannerScheduleReload();
        this.setupCustomButtonListeners();
        this.setupMutationObserver(document.body);
      } else {
        requestAnimationFrame(() => this.initialize());
      }
    }

    loadVariantData() {
      const variantsScript = document.getElementById("variants_js");
      if (variantsScript) {
        try {
          this.variantData = JSON.parse(variantsScript.textContent);
        } catch (e) {
          // Silent fail - variant data not available
        }
      }
    }

    setupVariantListener(attempts = 0) {
      if (attempts >= this.maxAttempts) return;

      // Listen for variant changes in product banner
      const productBanner = document.querySelector("ff-product-banner");
      if (!productBanner) {
        // Retry if product banner isn't loaded yet
        requestAnimationFrame(() => this.setupVariantListener(attempts + 1));
        return;
      }

      // Listen to variant-selects and variant-radios custom elements
      const setupVariantElements = () => {
        const variantElements = productBanner.querySelectorAll(
          "variant-selects, variant-radios"
        );
        variantElements.forEach((element) => {
          if (element.dataset.variantListenerSetup) return;
          element.dataset.variantListenerSetup = "true";

          // Listen for change events on the variant element
          element.addEventListener("change", () => {
            // Get variant ID from the input field (updated by variants.js)
            const variantInput = element
              .closest("ff-product-banner")
              ?.querySelector('input[name="id"]');
            if (variantInput && variantInput.value) {
              this.handleVariantChange(variantInput.value);
            }
          });

          // Check initial variant after a short delay to allow initialization
          setTimeout(() => {
            const variantInput = element
              .closest("ff-product-banner")
              ?.querySelector('input[name="id"]');
            if (variantInput && variantInput.value) {
              this.handleVariantChange(variantInput.value);
            }
          }, 100);
        });
      };

      // Setup existing elements
      setupVariantElements();

      // Also listen for changes on variant input fields directly
      const setupVariantInputs = () => {
        const variantInputs =
          productBanner.querySelectorAll('input[name="id"]');
        variantInputs.forEach((input) => {
          if (input.dataset.variantListenerSetup) return;
          input.dataset.variantListenerSetup = "true";

          input.addEventListener("change", () => {
            if (input.value) {
              this.handleVariantChange(input.value);
            }
          });

          // Check initial value
          if (input.value) {
            this.handleVariantChange(input.value);
          }
        });
      };

      setupVariantInputs();

      // Use MutationObserver to catch dynamically added variant elements
      const observer = new MutationObserver(() => {
        setupVariantElements();
        setupVariantInputs();
      });

      observer.observe(productBanner, {
        childList: true,
        subtree: true,
      });

      this.variantObserver = observer;
    }

    handleVariantChange(variantId) {
      // Don't update if a package button was explicitly selected
      if (this.packageButtonSelected) return;

      if (!variantId) return;

      if (!this.variantData) {
        this.loadVariantData();
      }

      if (!this.variantData) return;

      const variantIdNum = parseInt(variantId);
      if (isNaN(variantIdNum)) return;

      const variant = this.variantData.find((v) => v.id === variantIdNum);
      if (!variant) return;

      // Extract variant information
      const variantTitle = variant.title || "";

      // Format price - handle both Shopify.formatMoney and fallback
      let variantPrice = defaultConfig.servicePrice;
      if (variant.price) {
        if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
          const moneyFormat = window.money_format || "${{amount}}";
          variantPrice = Shopify.formatMoney(variant.price, moneyFormat);
          // Extract just the price text if formatMoney returns HTML
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = variantPrice;
          variantPrice =
            tempDiv.textContent || tempDiv.innerText || variantPrice;
        } else {
          // Fallback: format manually
          const priceInDollars = (variant.price / 100).toFixed(2);
          variantPrice = `$${priceInDollars}`;
        }
      }

      // Update config based on variant
      // Use variant title or extract duration from title
      let serviceTitle = defaultConfig.serviceTitle;
      if (variantTitle) {
        // Try to extract duration from variant title (e.g., "45-Minute", "60 Min", "60 Min.")
        const durationMatch = variantTitle.match(
          /(\d+)[\s-]*(?:min|minute|min\.)/i
        );
        if (durationMatch) {
          serviceTitle = `${durationMatch[1]}-Minute Floral Expert Consultation`;
        } else {
          // Use variant title as service title
          serviceTitle = `${variantTitle} Consultation`;
        }
      }

      this.config.serviceTitle = serviceTitle;
      this.config.servicePrice = variantPrice;
    }

    setupPackageButtonListeners() {
      // Let the CTA href (?variant=<id>&open_calendar=true) navigate so EAB re-opens against it.
      document.addEventListener("click", (event) => {
        const button = event.target.closest(
          selectors.consultationPackageButton
        );
        if (button) {
          this.handlePackageSelection(button);
        }
      });
    }

    setupBannerScheduleReload() {
      // Force the package-card reload on the banner CTA so EAB re-opens against the selected variant.
      document.addEventListener(
        "click",
        (event) => {
          if (!event.isTrusted) return;
          const button = event.target.closest(
            "ff-product-banner .calendar-btn"
          );
          if (!button) return;

          const banner = button.closest("ff-product-banner");
          const variantInput = banner?.querySelector('input[name="id"]');
          const variantId = variantInput && variantInput.value;
          if (!variantId) return;

          const params = new URLSearchParams(window.location.search);
          if (
            params.get("open_calendar") === "true" &&
            params.get("variant") === variantId
          ) {
            return;
          }

          event.preventDefault();
          event.stopImmediatePropagation();
          const url = new URL(window.location.href);
          url.searchParams.set("variant", variantId);
          url.searchParams.set("open_calendar", "true");
          window.location.href = url.toString();
        },
        true
      );
    }

    setupCustomButtonListeners() {
      const customButtons = document.querySelectorAll(
        selectors.customButtonSelector
      );
      customButtons.forEach((button) => {
        button.addEventListener("click", (e) => {
          e.preventDefault();
          const scheduleButton = document.querySelector(
            selectors.scheduleButtonSelector
          );
          if (scheduleButton) {
            scheduleButton.click();
          }
        });
      });
    }

    handlePackageSelection(button) {
      const packageName = button.dataset.packageName || "";
      const packagePrice =
        button.dataset.packagePrice || defaultConfig.servicePrice;
      const videoCallDuration = button.dataset.videoCallDuration || "";

      // Mark that package button was selected (takes priority over variant)
      this.packageButtonSelected = true;

      // Update config with selected package data
      this.config.serviceTitle = videoCallDuration
        ? `${videoCallDuration} Floral Expert Consultation`
        : packageName || defaultConfig.serviceTitle;
      this.config.servicePrice = packagePrice;

      // Store package data for later use
      this.selectedPackage = {
        name: packageName,
        price: packagePrice,
        videoCallDuration: videoCallDuration,
      };
    }

    setupMutationObserver(targetNode) {
      const config = { childList: true, subtree: true };

      const observer = new MutationObserver((mutationsList) => {
        const hasRelevantChanges = mutationsList.some(
          (mutation) =>
            mutation.type === "childList" && mutation.addedNodes.length > 0
        );

        if (hasRelevantChanges) {
          const servicifyModal = document.querySelector(
            selectors.servicifyModal
          );
          if (servicifyModal) {
            this.processNodesForButtons(mutationsList);
          }
        }
      });

      this.checkExistingElements();

      observer.observe(targetNode, config);
      this.observer = observer;
    }

    checkExistingElements() {
      const overlays = document.querySelectorAll(selectors.overlaySelector);
      if (overlays.length > 0) {
        overlays.forEach((overlay) => this.preventOverlayEvent(overlay));
      }

      const button = document.querySelector(selectors.buttonSelector);
      if (button) {
        this.updateCart();
        this.appendCustomHTML();
      }
    }

    processNodesForButtons(mutationsList) {
      for (const mutation of mutationsList) {
        if (mutation.type === "childList") {
          if (mutation.target.classList.contains("ReactModalPortal")) {
            requestAnimationFrame(() => {
              this.findOverlay();
              this.findButton();
            });
          }
        }
      }
    }

    preventOverlayEvent(overlayElement) {
      if (overlayElement.dataset.serviceifyOverlayEventStopped) {
        return;
      }

      overlayElement.addEventListener(
        "click",
        function (event) {
          if (event.target === event.currentTarget) {
            event.stopImmediatePropagation();
            event.preventDefault();
          }
        },
        false
      );

      overlayElement.dataset.serviceifyOverlayEventStopped = "true";
    }

    findOverlay(attempts = 0) {
      if (attempts >= this.maxAttempts) return;
      const overlays = document.querySelectorAll(selectors.overlaySelector);

      if (overlays.length > 0) {
        overlays.forEach((overlay) => {
          this.preventOverlayEvent(overlay);
        });
      } else {
        this.findOverlay(attempts + 1);
      }
    }

    findButton(attempts = 0) {
      if (attempts >= this.maxAttempts) return;
      const button = document.querySelector(selectors.buttonSelector);
      if (button) {
        this.updateCart();
        this.appendCustomHTML();
      } else {
        this.findButton(attempts + 1);
      }
    }

    getSelectedConsultationDisplay() {
      // Resolve the selected variant's name/price from the package cards.
      const variantInput = document.querySelector(
        'ff-product-banner input[name="id"]'
      );
      const variantId = variantInput && variantInput.value;
      const card = variantId
        ? document.querySelector(
            `.js-consultation-package-button[data-variant-id="${variantId}"]`
          )
        : null;
      return {
        title: (card && card.dataset.packageName) || this.config.serviceTitle,
        price: (card && card.dataset.packagePrice) || this.config.servicePrice,
      };
    }

    appendCustomHTML(attempts = 0) {
      if (attempts >= this.maxAttempts) return;
      const container = document.querySelector(
        ".ReactModal__Content.ReactModal__Content--after-open:has([data-servicify=checkout]) .servicify-modal-close+div>div>:nth-child(2)"
      );
      if (container) {
        const { title, price } = this.getSelectedConsultationDisplay();
        const custom = document.createElement("div");
        custom.setAttribute(
          "style",
          "display:flex;justify-content:space-between;align-items:center;padding:0 1.5em;"
        );
        const titleEl = document.createElement("div");
        titleEl.textContent = title;
        const priceEl = document.createElement("div");
        priceEl.textContent = price;
        custom.append(titleEl, priceEl);
        container.insertAdjacentElement("beforeend", custom);
      } else {
        this.appendCustomHTML(attempts + 1);
      }
    }

    updateCartHTML() {
      const headerCart = document.querySelector("[data-cart-sidebar]");
      if (!headerCart || !this.cart) return;
      headerCart.innerHTML = this.cart.innerHTML;
    }

    updateCart() {
      Shopify.getCart((cart) => {
        window.halo.updateSidebarCart(cart);
        this.updateCartHTML();
      });
    }

    cleanup() {
      if (this.variantObserver) {
        this.variantObserver.disconnect();
        this.variantObserver = null;
      }
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
  }

  window.ServiceifyAddToCartHandlerInitialized = true;
  new ServiceifyAddToCartHandler();
})();
